import React from "react";

import { DomainCell } from "@common/components/ShakeTable/ui/activityHistory/DomainCell";
import { MessageCell } from "@common/components/ShakeTable/ui/activityHistory/MessageCell";
import { MethodCell } from "@common/components/ShakeTable/ui/activityHistory/MethodCell";
import { SizeCell } from "@common/components/ShakeTable/ui/activityHistory/SizeCell";
import { StatusCell } from "@common/components/ShakeTable/ui/activityHistory/StatusCell";
import { TimeCell } from "@common/components/ShakeTable/ui/activityHistory/TimeCell";
import { TimestampCell } from "@common/components/ShakeTable/ui/activityHistory/TimestampCell";
import { TypeCell } from "@common/components/ShakeTable/ui/activityHistory/TypeCell";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";

import { LogUnionRowItem } from "../../MasterTable/models/MasterTableModel";
import { HeaderCell } from "../ui/BaseTableStyles";
import { useBaseTable } from "../useBaseTable";

type TableProps = {
  data: LogUnionRowItem[];
  onRowClick: (row: LogUnionRowItem) => void;
};

export function useActivityTable({ data, onRowClick }: TableProps) {
  const [flaggedIds, setFlaggedIds] = React.useState<string[]>([]);

  const baseTableProps = useBaseTable({
    data,
    columns: defaultActivityColumns as unknown as ColumnDef<LogUnionRowItem>[], // TODO FIX TYPING
    selectable: true,
    hasMore: false,
    isLoading: false,
    isFetchingNext: false,
    loadMore: () => {
      return;
    },
    onRowClick,
    onPreviewRequest: () => {
      /// Space to preview
      return;
    },
    onDeleteRequest: () => {
      return;
    },
    withBorder: true,
    flaggedIds: flaggedIds,
  });

  return {
    ...baseTableProps,
    setFlaggedRows: (ids: string[]) => setFlaggedIds(ids),
  };
}

const columnHelper = createColumnHelper<LogUnionRowItem>();

const defaultActivityColumns = [
  columnHelper.accessor("timestamp", {
    cell: (info) => <TimestampCell info={info} />,
    header: (info) => (
      <HeaderCell $withBorder={info.table.getSelectedRowModel()?.rows[0]?.index === 0}>{"Time"}</HeaderCell>
    ),
    meta: {
      columnTemplate: "105px",
    },
  }),
  columnHelper.accessor("message", {
    cell: (info) => <MessageCell info={info} />,
    header: ({ table }) => (
      <HeaderCell $withBorder={table.getSelectedRowModel()?.rows[0]?.index === 0}>{"Name"}</HeaderCell>
    ),
  }),
  columnHelper.accessor("method", {
    cell: (info) => <MethodCell info={info} />,
    header: (info) => (
      <HeaderCell $withBorder={info.table.getSelectedRowModel()?.rows[0]?.index === 0}>{"Method"}</HeaderCell>
    ),
    meta: {
      columnTemplate: "80px",
    },
  }),
  columnHelper.accessor("status_code", {
    cell: (info) => <StatusCell info={info} />,
    header: (info) => (
      <HeaderCell $withBorder={info.table.getSelectedRowModel()?.rows[0]?.index === 0}>{"Status"}</HeaderCell>
    ),
    meta: {
      columnTemplate: "80px",
    },
  }),
  columnHelper.accessor("type", {
    cell: (info) => <TypeCell info={info} />,
    header: (info) => (
      <HeaderCell $withBorder={info.table.getSelectedRowModel()?.rows[0]?.index === 0}>{"Type"}</HeaderCell>
    ),
    meta: {
      columnTemplate: "150px",
    },
  }),
  columnHelper.accessor("url", {
    cell: (info) => <DomainCell info={info} />,
    header: (info) => (
      <HeaderCell $withBorder={info.table.getSelectedRowModel()?.rows[0]?.index === 0}>{"Domain"}</HeaderCell>
    ),
    meta: {
      columnTemplate: "300px",
    },
  }),
  columnHelper.accessor("response_body", {
    cell: (info) => <SizeCell info={info} />,
    header: (info) => (
      <HeaderCell $withBorder={info.table.getSelectedRowModel()?.rows[0]?.index === 0}>{"Size"}</HeaderCell>
    ),
    meta: {
      columnTemplate: "150px",
    },
  }),
  columnHelper.accessor("duration", {
    cell: (info) => <TimeCell info={info} />,
    header: (info) => (
      <HeaderCell $withBorder={info.table.getSelectedRowModel()?.rows[0]?.index === 0}>{"Time"}</HeaderCell>
    ),
    meta: {
      columnTemplate: "100px",
    },
  }),
];
